import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import { graphql } from "lib/generated/app-service-gql";
import {
  UploadCustomerDocumentUrlMutation,
  UploadCustomerDocumentUrlMutationVariables,
} from "lib/generated/app-service-gql/graphql";
import { useGraphQlMutation } from "../useGraphQlClient";

type UploadDocumentVariables = {
  file: File;
  resourceId?: string;
  onProgress: (progress: number) => void;
};

/**
 * Uploads a customer document to the signed s3 url retrieved from
 * the @see useCustomerDocumentUploadUrl hook below
 * @param options
 * @returns
 */
export function useStoreCustomerDocument(
  options?: UseMutationOptions<string, AxiosError, UploadDocumentVariables, unknown>,
) {
  const { mutateAsync: getUrl } = useCustomerDocumentUploadUrl();
  return useMutation<string, AxiosError, UploadDocumentVariables>(
    async ({ file, resourceId, onProgress }) => {
      const {
        uploadCustomerDocument: { signedUrl, s3DocumentId },
      } = await getUrl({
        input: { resourceId, fileName: file.name, mimeType: file.type },
      });

      await axios.put(signedUrl, file, {
        headers: {
          "Content-Type": "application/octet-stream",
          "Access-Control-Allow-Origin": "*",
        },
        onUploadProgress(progressEvent) {
          progressEvent.progress && onProgress(Math.floor(progressEvent.progress * 100));
        },
      });

      return s3DocumentId;
    },
    options,
  );
}

const uploadCustomerDoc = graphql(`
  mutation UploadCustomerDocumentURL($input: UploadCustomerDocumentRequest!) {
    uploadCustomerDocument(UploadCustomerDocumentRequest: $input) {
      signedUrl
      s3DocumentId
    }
  }
`);

/**
 * Gets the s3 bucket URL for a customer document
 * @param options
 * @returns
 */
export function useCustomerDocumentUploadUrl(
  options?: UseMutationOptions<
    UploadCustomerDocumentUrlMutation,
    unknown,
    UploadCustomerDocumentUrlMutationVariables,
    unknown
  >,
) {
  const { data, ...rest } = useGraphQlMutation(uploadCustomerDoc, {
    ...options,
    onSuccess: (data, vars, ctx) => {
      options?.onSuccess?.(data, vars, ctx);
    },
  });
  return { data: data?.uploadCustomerDocument, ...rest };
}
